import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../../models/item-modifier-ui.model';
import { UiService } from '../../../../services/ui.service';
import { ModifierGroupService } from 'services/modifier-group.service';
import { unawaited } from 'utility/unawaited';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { RxUtil } from 'utility/rx.util';
import { NestedModGroupSheetStore } from 'stores/nested-mod-group-sheet.store';

interface NestedItemModifierGroupUI extends ItemModifierGroupUI {
  parentMod?: ItemModifierUI;
}
@Component({
  selector: 'v2-nested-modifiers-selection',
  templateUrl: 'v2-nested-modifiers-selection.component.html',
  styleUrls: ['v2-nested-modifiers-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class V2NestedModifierSelection implements OnInit {
  @Input() modifier?: ItemModifierUI;

  private readonly onChanges$ = new BehaviorSubject<void>(undefined);

  constructor(
    public uiService: UiService,
    public modifierGroupService: ModifierGroupService,
    public nestedModGroupSheetStore: NestedModGroupSheetStore
  ) {}

  ngOnInit(): void {
    setInterval(() => this.onChanges$.next(), 500);
  }

  readonly selectedNestedModifierGroups$: Observable<
    NestedItemModifierGroupUI[]
  > = this.onChanges$.pipe(
    map(() =>
      this.modifierGroupService.getSelectedNestedModifierGroups(this.modifier)
    )
  );

  readonly invalidSelectionGroups$: Observable<NestedItemModifierGroupUI[]> =
    this.onChanges$.pipe(
      map(() => this.modifier),
      filter(RxUtil.inputIsNotNullOrUndefined),
      map((modifier) => this.getInvalidSelectionGroups(modifier))
    );

  private getInvalidSelectionGroups(
    mod: ItemModifierUI,
    invalidSelectionGroups: NestedItemModifierGroupUI[] = []
  ): NestedItemModifierGroupUI[] {
    mod.modifierGroups.forEach((group) => {
      if (group.isSelectionValid) {
        group.modifiers
          .filter((mod) => mod.isSelected)
          .forEach((mod) =>
            this.getInvalidSelectionGroups(mod, invalidSelectionGroups)
          );
      } else {
        if (!invalidSelectionGroups.some((e) => e.id === group.id)) {
          invalidSelectionGroups.push({ ...group, parentMod: mod });
        }
      }
    });
    return invalidSelectionGroups;
  }

  readonly isSelectionInvalid$: Observable<boolean> =
    this.invalidSelectionGroups$.pipe(
      map((invalidSelectionGroups) => invalidSelectionGroups.length > 0)
    );

  openSheet(modGroup: NestedItemModifierGroupUI): void {
    this.nestedModGroupSheetStore.clearCurrentModifierGroupSheetParentModifierStack();

    if (modGroup.parentMod) {
      unawaited(
        this.modifierGroupService.openNestedModifierGroupSheet(
          modGroup.parentMod
        )
      );
      this.nestedModGroupSheetStore.currentScrollBottomSheetToElementById =
        modGroup.id;
    }
  }

  async openUnfilledSheet(): Promise<void> {
    const invalidSelectionGroups = await RxUtil.takeOne(
      this.invalidSelectionGroups$
    );

    if (invalidSelectionGroups[0]) {
      this.openSheet(invalidSelectionGroups[0]);
    }
  }

  async openTopLevelSheet(): Promise<void> {
    if (this.modifier) {
      unawaited(
        this.modifierGroupService.openNestedModifierGroupSheet(this.modifier)
      );
    }
  }

  readonly requiredSelectionText$: Observable<string> =
    this.invalidSelectionGroups$.pipe(
      map((invalidSelectionGroups) => {
        let text = 'Multiple selections';
        if (invalidSelectionGroups.length === 1) {
          text =
            invalidSelectionGroups[0].guestInstructions ||
            invalidSelectionGroups[0].title;
        }
        return text;
      })
    );
}
